import { createRootRoute, createRoute, createRouter } from "@tanstack/react-router"
import { useTranslation } from "react-i18next"
import { useAtomValue } from "jotai"
import { useEffect, useRef } from "react"
import App from "@/components/App"
import Home from "@/components/Home"
import Project from "@/components/Project"
import { projectAtom, projectsAtom } from "@/store/project/projects-state"
import { store } from "@/store/store"
import { routes } from "@/utils/routes"

import { projectSessionAtom, useProjectSession } from "@/routing/project-session"

const toChildPath = (path: string) => (path === "/" ? "/" : path.replace(/^\//, ""))

const rootRoute = createRootRoute({
	component: App,
})

const homeRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: routes.root.path,
	component: HomeRouteComponent,
})

const homeChildRouteConfigs = [
	routes.styles.path,
	routes.styleCreate.path,
	routes.styleDetail.path,
	routes.tutorials.path,
	routes.support.path,
	routes.settings.path,
	routes.account.path,
	routes.projects.path,
]

const homeChildRoutes = homeChildRouteConfigs.map((path) =>
	createRoute({
		getParentRoute: () => rootRoute,
		path: toChildPath(path),
		component: HomeRouteComponent,
	}),
)

const isProjectRoutePath = (path: string): path is "projects/$projectId" =>
	path === "projects/$projectId"

const PROJECT_ROUTE_CHILD_PATH = (() => {
	const path = toChildPath(routes.project.path)
	if (!isProjectRoutePath(path)) {
		throw new Error(`Unexpected project route path: ${path}`)
	}

	return path
})()

const projectRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: PROJECT_ROUTE_CHILD_PATH,
	loader: () => {
		return { status: "ok" as const }
	},
	component: ProjectRouteComponent,
})

const routeTree = rootRoute.addChildren([homeRoute, ...homeChildRoutes, projectRoute])

const routerInstance = createRouter({
	routeTree,
})

export const router = routerInstance

declare module "@tanstack/react-router" {
	interface Register {
		router: typeof routerInstance
	}
}

function HomeRouteComponent() {
	return <Home />
}

function ProjectRouteComponent() {
	const { t } = useTranslation("components")
	const { projectId = "" } = projectRoute.useParams()
	const project = useAtomValue(projectAtom)
	const { data: projects, isInitialLoading } = useAtomValue(projectsAtom)
	const { open } = useProjectSession()
	const attemptedOpenRef = useRef<string | null>(null)
	const sessionState = useAtomValue(projectSessionAtom)

	useEffect(() => {
		if (attemptedOpenRef.current !== projectId) {
			attemptedOpenRef.current = null
		}
	}, [projectId])

	useEffect(() => {
		const targetProject = projects.find((candidate) => candidate.id === projectId)
		if (!targetProject) {
			// If projects have finished loading and the project doesn't exist, redirect to home
			if (!isInitialLoading && project?.id !== projectId) {
				void import("@/router").then(({ router }) => {
					void routes.root.navigate(router)
				})
			}
			return
		}

		// closeSession() clears projectAtom before route navigation completes.
		// While we are still on the project route for the same id, avoid re-opening.
		if (sessionState.status === "closing" && sessionState.projectId === projectId) {
			return
		}

		// If an open for this project is already in flight, avoid duplicate opens.
		if (sessionState.status === "opening" && sessionState.projectId === projectId) {
			return
		}

		if (project?.id === targetProject.id && sessionState.status !== "idle") {
			return
		}

		if (attemptedOpenRef.current === projectId) {
			return
		}

		attemptedOpenRef.current = projectId

		const exitProjectRouteIfUnopened = () => {
			// If opening completed/failed without setting the requested project,
			// leave the project route so we don't stay on "Opening project..." forever.
			if (attemptedOpenRef.current !== projectId) {
				return
			}

			// During create/switch flows, session state can already be opening/open for
			// this project while projectAtom is still transitioning. In that case, do
			// not force navigation back to root.
			const sessionStateAfterOpen = store.get(projectSessionAtom)
			if (
				sessionStateAfterOpen.projectId === projectId &&
				(sessionStateAfterOpen.status === "opening" ||
					sessionStateAfterOpen.status === "open")
			) {
				return
			}

			const openedProject = store.get(projectAtom)
			if (openedProject?.id === projectId) {
				return
			}

			void import("@/router").then(({ router }) => {
				if (router.state.location.pathname === routes.project.build({ projectId })) {
					void routes.root.navigate(router)
				}
			})
		}

		open({ project: targetProject })
			.then(() => {
				exitProjectRouteIfUnopened()
			})
			.catch((error) => {
				console.error("Failed to open project via session", error)
				exitProjectRouteIfUnopened()
			})
			.finally(() => {
				// Allow retries for the same project id after this attempt settles.
				if (attemptedOpenRef.current === projectId) {
					attemptedOpenRef.current = null
				}
			})
	}, [projectId, project?.id, projects, open, isInitialLoading, sessionState])

	if (project?.id !== projectId) {
		return (
			<div className="flex flex-1 items-center justify-center text-sm text-base-400">
				{t("router.openingProject")}
			</div>
		)
	}

	return <Project />
}
